import React, { useState, useEffect } from "react";
import { Download, ArrowLeft, Loader, ZoomIn, ZoomOut } from "lucide-react";

export const UpscaleResult = ({ processedImage, originalImage, isLoading, onBackClick }) => {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(100);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (processedImage && !isLoading) {
      const img = new Image();
      img.src = processedImage;
      img.onload = () => setImageLoaded(true);
    } else {
      setImageLoaded(false);
    }
  }, [processedImage, isLoading]);

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = processedImage;
    link.download = "upscaled-image.png";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDrag = (event) => {
    const container = document.getElementById("image-container");
    if (!container) return;

    const containerRect = container.getBoundingClientRect();
    let newPosition = ((event.clientX - containerRect.left) / containerRect.width) * 100;
    newPosition = Math.max(0, Math.min(100, newPosition));
    setSliderPosition(newPosition);
  };

  const handleTouchMove = (event) => {
    const container = document.getElementById("image-container");
    if (!container || event.touches.length === 0) return;

    const touch = event.touches[0];
    const containerRect = container.getBoundingClientRect();
    let newPosition = ((touch.clientX - containerRect.left) / containerRect.width) * 100;
    newPosition = Math.max(0, Math.min(100, newPosition));
    setSliderPosition(newPosition);
  };

  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
    document.addEventListener("mousemove", handleDrag);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleTouchStart = () => {
    setIsDragging(true);
    document.addEventListener("touchmove", handleTouchMove);
    document.addEventListener("touchend", handleTouchEnd);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    document.removeEventListener("mousemove", handleDrag);
    document.removeEventListener("mouseup", handleMouseUp);
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    document.removeEventListener("touchmove", handleTouchMove);
    document.removeEventListener("touchend", handleTouchEnd);
  };

  const handleZoomChange = (e) => {
    setZoomLevel(parseFloat(e.target.value));
  };

  return (
    <div className="flex-1 h-full w-full overflow-auto relative z-10 bg-gradient-to-b from-blue-50 to-white">
      {/* Header */}
      <header className="w-full mx-auto py-2 sm:py-3 px-4 sm:px-6 bg-white shadow-md border-b border-blue-100 sticky top-0 z-20">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-0">
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <button
              onClick={onBackClick}
              className="p-1 sm:p-2 rounded-full hover:bg-blue-50 transition-colors text-blue-600"
              aria-label="Go back"
            >
              <ArrowLeft size={20} className="sm:w-6 sm:h-6" />
            </button>
            <h1 className="text-xl sm:text-2xl md:text-3xl font-semibold bg-gradient-to-r from-violet-600 to-blue-500 text-transparent bg-clip-text">
              Image Upscaler Result
            </h1>
          </div>
          
          {/* Zoom Controls */}
          <div className="flex items-center gap-2 sm:gap-3 bg-blue-50 p-1 sm:p-2 rounded-lg w-full sm:w-auto">
            <button 
              onClick={() => setZoomLevel(Math.max(50, zoomLevel - 10))}
              className="text-blue-600 hover:bg-blue-100 p-1 rounded-full transition-colors"
            >
              <ZoomOut size={16} className="sm:w-5 sm:h-5" />
            </button>
            <div className="flex items-center gap-1 sm:gap-2 flex-1">
              <input
                type="range"
                min="50"
                max="200"
                value={zoomLevel}
                onChange={handleZoomChange}
                className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer"
                style={{ accentColor: '#4f46e5' }}
              />
              <span className="text-xs sm:text-sm text-blue-700 font-medium min-w-[40px] sm:min-w-[50px]">
                {zoomLevel}%
              </span>
            </div>
            <button 
              onClick={() => setZoomLevel(Math.min(200, zoomLevel + 10))}
              className="text-blue-600 hover:bg-blue-100 p-1 rounded-full transition-colors"
            >
              <ZoomIn size={16} className="sm:w-5 sm:h-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Main content */}
      <div className="w-full px-2 sm:px-4 lg:px-6 py-4 sm:py-6 flex flex-col items-center">
        <div className="bg-white rounded-xl shadow-lg p-3 sm:p-5 w-full mb-2 border border-blue-100">
          <h2 className="text-lg sm:text-xl font-semibold mb-3 sm:mb-4 text-gray-800 flex flex-col sm:flex-row items-start sm:items-center gap-2 border-b pb-3 border-blue-100">
            <span className="inline-flex items-center gap-2">
              <span className="p-1 rounded-full bg-gradient-to-r from-violet-600 to-blue-500 text-white">
                <ZoomIn size={16} className="sm:w-5 sm:h-5" />
              </span>
              Compare Results
            </span>
            <span className="text-xs sm:text-sm text-blue-600 font-normal ml-auto">
              Drag the slider to compare images
            </span>
          </h2>

          <div
            id="image-container"
            className="relative w-full h-48 sm:h-64 md:h-[50vh] lg:h-[60vh] overflow-hidden select-none rounded-lg border border-blue-200 bg-gradient-to-r from-gray-50 to-blue-50"
            style={{ cursor: isDragging ? "ew-resize" : "default" }}
          >
            {/* Original Image */}
            {originalImage && (
              <div
                className="absolute inset-0 flex items-center justify-center"
                style={{
                  transform: `scale(${zoomLevel / 100})`,
                  transformOrigin: "center",
                  filter: isLoading ? "blur(8px)" : "none",
                  transition: "filter 0.3s ease",
                }}
              >
                <img
                  src={originalImage}
                  alt="Original"
                  className="w-full h-full object-contain"
                />
              </div>
            )}

            {/* Loader */}
            {isLoading && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/70 backdrop-blur-sm z-20">
                <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-t-2 border-b-2 border-blue-500 mb-3 sm:mb-4"></div>
              </div>
            )}

            {/* Comparison view */}
            {imageLoaded && !isLoading && originalImage && processedImage && (
              <>
                {/* Upscaled Image */}
                <div
                  className="absolute inset-0 overflow-hidden"
                  style={{
                    clipPath: `inset(0 ${100 - sliderPosition}% 0 0)`,
                    transform: `scale(${zoomLevel / 100})`,
                    transformOrigin: "center",
                  }}
                >
                  <img
                    src={processedImage}
                    alt="Upscaled"
                    className="w-full h-full object-contain"
                  />
                </div>

                {/* Labels */}
                <div className="absolute top-2 sm:top-4 left-2 sm:left-4 bg-black/70 text-white px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs sm:text-sm font-medium">
                  Original
                </div>
                <div className="absolute top-2 sm:top-4 right-2 sm:right-4 bg-gradient-to-r from-violet-600 to-blue-500 text-white px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs sm:text-sm font-medium shadow-md">
                  Upscaled
                </div>

                {/* Drag Handle */}
                <div
                  className="absolute top-0 bottom-0 w-1 bg-white shadow-lg cursor-ew-resize flex items-center justify-center"
                  style={{ left: `${sliderPosition}%`, transform: "translateX(-50%)" }}
                  onMouseDown={handleMouseDown}
                  onTouchStart={handleTouchStart}
                >
                  <div className="bg-gradient-to-r from-violet-600 to-blue-500 text-white p-1 sm:p-2 rounded-full shadow-lg">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-6 sm:w-6" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
                      <path d="M15.59 16.59L10.42 12 15.59 7.41 14.17 6l-6 6 6 6 1.42-1.41z" transform="scale(-1, 1) translate(-24, 0)" />
                    </svg>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Download Button */} 
      <div className="flex justify-center gap-4 px-4 sm:px-6 py-4 sm:py-6 -mt-2 sm:-mt-4">
        <button
          onClick={handleDownload}
          disabled={!processedImage || isLoading || !imageLoaded}
          className="flex items-center gap-2 px-6 sm:px-8 py-2 sm:py-3 bg-gradient-to-r from-blue-500 to-indigo-700 text-white rounded-lg hover:from-blue-600 hover:to-indigo-800 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed transition-colors shadow-md text-sm sm:text-base w-full sm:w-auto justify-center"
        >
          <Download size={18} className="sm:w-5 sm:h-5" />
          Download Upscaled Image
        </button>
      </div>
      
      {/* Footer with stats */}
      {imageLoaded && !isLoading && originalImage && processedImage && (
        <div className="w-full px-4 sm:px-6 lg:px-8 py-4">
          <div className="mx-auto max-w-3xl bg-white rounded-lg shadow-md border border-blue-100 p-3 sm:p-4 flex flex-col sm:flex-row justify-around gap-3 sm:gap-0">
            <div className="text-center">
              <p className="text-xs sm:text-sm text-gray-500">Original Size</p>
              <p className="font-medium text-blue-700 text-sm sm:text-base">1024 x 768</p>
            </div>
            <div className="text-center border-t sm:border-l sm:border-t-0 sm:border-r border-blue-100 pt-3 sm:pt-0 sm:px-6">
              <p className="text-xs sm:text-sm text-gray-500">Upscaled Size</p>
              <p className="font-medium text-blue-700 text-sm sm:text-base">4096 x 3072</p>
            </div>
            <div className="text-center border-t sm:border-t-0 border-blue-100 pt-3 sm:pt-0">
              <p className="text-xs sm:text-sm text-gray-500">Enhancement Level</p>
              <p className="font-medium text-blue-700 text-sm sm:text-base">4x</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};